import * as React from 'react';
import { IconChevronSmallDown } from '@tock/icons';
import { MenuProps as MuiMenuProps } from '@material-ui/core';
import { isEmpty, keyBy } from 'es-toolkit/compat';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import { useMultiChipSelectStyles } from './multiChipSelectStyles';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Select, { SelectProps as MuiSelectProps } from '@material-ui/core/Select';
import clsx from 'clsx';

export interface MuiMultiChipSelectProps extends Pick<
  MuiSelectProps,
  Exclude<keyof MuiSelectProps, 'onChange'>
> {
  itemGroups: SelectOption[][];
  selectedItems: SelectOption[];
  inputLabel: string;
  id: string;
  onChange(item: SelectOption): void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuPropsDefault: Partial<MuiMenuProps> = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  MenuListProps: {
    style: {
      padding: '1.2rem',
    },
  },
};

const MultiChipSelect = ({
  selectedItems,
  itemGroups,
  onChange,
  inputLabel,
  id,
  MenuProps,
  value: someValue,
  ...rest
}: MuiMultiChipSelectProps) => {
  const classes = useMultiChipSelectStyles();

  const selectedItemsByKey = keyBy(selectedItems, 'key');

  const renderValue = (selected: unknown) => {
    if (isEmpty(selected)) {
      return <div className={classes.emptyStateLabel}>{inputLabel}</div>;
    }
    const selectedOptions = (selected as string[]).map(
      (selectedKey) => selectedItemsByKey[selectedKey]
    );
    return (
      <div className={classes.chips}>
        {selectedOptions.map((option) => (
          <Chip key={option.key} label={option.label} className={classes.chip + ' selected'} />
        ))}
      </div>
    );
  };

  return (
    <FormControl variant="outlined" className={classes.formControl}>
      <Select
        classes={{ root: classes.root }}
        labelId={`multiple-chip-label-${id}`}
        id={id}
        value={selectedItems.map((selectedItem) => selectedItem.key)}
        renderValue={renderValue}
        MenuProps={{ ...MenuPropsDefault, ...MenuProps }}
        onClick={preventBubbleOutside}
        multiple
        displayEmpty
        className={
          selectedItems && selectedItems.length > 0
            ? classes.selectedChips
            : classes.noSelectedChips
        }
        IconComponent={IconChevronSmallDown}
        inputProps={{
          'aria-label': inputLabel,
        }}
        {...rest}
      >
        {itemGroups.map((items: SelectOption[], idx: number) => (
          <ChipSection
            key={`chipSection-${idx}`}
            items={items}
            onChange={onChange}
            selectedItemsByKey={selectedItemsByKey}
          />
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiChipSelect;

interface ChipSectionProps {
  items: SelectOption[];
  onChange: (item: SelectOption) => void;
  selectedItemsByKey: Dictionary<SelectOption>;
}

const ChipSection = React.forwardRef<HTMLDivElement, ChipSectionProps>(
  ({ items, onChange, selectedItemsByKey }, ref) => {
    const classes = useMultiChipSelectStyles();
    return (
      <>
        <div className={classes.sectionDivider} />
        {items.map((item: SelectOption) => (
          <Chip
            ref={ref}
            key={item.key}
            label={item.label}
            className={clsx(classes.chip, { selected: !!selectedItemsByKey[item.key] })}
            onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              preventBubbleOutside(event);
              onChange(item);
            }}
            tabIndex={0}
            onKeyPress={(event: React.KeyboardEvent) => {
              if (event.key === 'Enter') {
                onChange(item);
              }
            }}
          />
        ))}
      </>
    );
  }
);
